import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'wouter';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { 
  ArrowLeft,
  ChevronLeft, 
  ChevronRight, 
  CheckCircle2, 
  Maximize, 
  Minimize,
  X,
  FileText,
  Book,
  CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

// Key takeaway component for highlighting important points
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="relative bg-gradient-to-r from-blue-50 via-indigo-50 to-slate-50 border border-blue-200 rounded-lg p-4 my-4 overflow-hidden">
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500 to-indigo-600"></div>
      <div className="flex items-start">
        <div className="flex-shrink-0 mt-1">
          <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
            <CheckCircle2 className="h-3 w-3 text-white" />
          </div>
        </div>
        <div className="ml-3">
          <h4 className="text-sm font-bold text-slate-900 mb-1 tracking-tight">Key Takeaway</h4>
          <p className="text-slate-700 leading-relaxed text-xs">{children}</p>
        </div>
      </div>
    </div>
  );
};

// Clinical Note component
const ClinicalNote = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="relative bg-gradient-to-r from-amber-50 via-orange-50 to-yellow-50 border border-amber-200 rounded-lg p-4 my-4 overflow-hidden">
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-amber-500 to-orange-600"></div>
      <div className="flex items-start">
        <div className="flex-shrink-0 mt-1">
          <div className="w-6 h-6 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center">
            <CheckCircle2 className="h-3 w-3 text-white" />
          </div>
        </div>
        <div className="ml-3">
          <h4 className="text-sm font-bold text-slate-900 mb-1 tracking-tight">Clinical Note</h4>
          <p className="text-slate-700 leading-relaxed text-xs">{children}</p>
        </div>
      </div>
    </div>
  );
};

// Evidence component
const Evidence = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="relative bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 border border-green-200 rounded-lg p-4 my-4 overflow-hidden">
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-green-500 to-emerald-600"></div>
      <div className="flex items-start">
        <div className="flex-shrink-0 mt-1">
          <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
            <CheckCircle2 className="h-3 w-3 text-white" />
          </div>
        </div>
        <div className="ml-3">
          <h4 className="text-sm font-bold text-slate-900 mb-1 tracking-tight">Evidence</h4>
          <p className="text-slate-700 leading-relaxed text-xs">{children}</p>
        </div>
      </div>
    </div>
  );
};

// Slide interface
interface Slide {
  id: number;
  title: string;
  content: React.ReactNode;
  image?: string;
}

// Slideshow component
const Slideshow = ({ slides }: { slides: Slide[] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const slideshowRef = useRef<HTMLDivElement>(null);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };
  
  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (slideshowRef.current?.requestFullscreen) {
        slideshowRef.current.requestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  // Add fullscreen change event listener and keyboard navigation
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isFullscreen) {
        if (e.key === 'ArrowRight' || e.key === ' ') {
          e.preventDefault();
          nextSlide();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          prevSlide();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          toggleFullscreen();
        }
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);

  return (
    <div 
      ref={slideshowRef}
      className={`relative bg-white rounded-lg overflow-hidden shadow-xl border border-slate-200 ${
        isFullscreen ? 'w-screen h-screen' : 'w-full max-w-6xl mx-auto'
      }`}
    >
      {/* Slide content */}
      <div className={`${isFullscreen ? 'h-screen' : 'h-96 md:h-[32rem]'} flex items-center justify-center p-6 md:p-8`}>
        <div className="w-full max-w-4xl">
          <div className="text-center space-y-4">
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-slate-900 leading-tight">
              {slides[currentSlide].title}
            </h2>
            <div className="text-slate-700 text-sm md:text-base max-w-3xl mx-auto">
              {slides[currentSlide].content}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation controls */}
      <div className="absolute inset-0 flex items-center justify-between p-2 md:p-4 pointer-events-none">
        <Button
          variant="ghost"
          size="sm"
          onClick={prevSlide}
          className="pointer-events-auto bg-slate-100 hover:bg-slate-200 text-slate-700 border-none h-10 w-10 p-0 rounded-full"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={nextSlide}
          className="pointer-events-auto bg-slate-100 hover:bg-slate-200 text-slate-700 border-none h-10 w-10 p-0 rounded-full"
        >
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {/* Top controls */}
      <div className="absolute top-2 md:top-4 right-2 md:right-4 flex gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleFullscreen}
          className="bg-slate-100 hover:bg-slate-200 text-slate-700 border-none h-8 w-8 p-0 rounded-full"
        >
          {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
        </Button>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-2 md:bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentSlide
                ? 'bg-blue-600 scale-125'
                : 'bg-slate-300 hover:bg-slate-400'
            }`}
          />
        ))}
      </div>

      {/* Slide counter */}
      <div className="absolute bottom-2 md:bottom-4 right-2 md:right-4 text-slate-600 text-xs bg-slate-100 px-2 py-1 rounded">
        {currentSlide + 1} / {slides.length}
      </div>
    </div>
  );
};

// Slide data
const slides: Slide[] = [
  {
    id: 1,
    title: "The New Frontier in Proactive Medicine: Leveraging GLP-1 Agonists for Healthspan Optimization",
    content: (
      <div className="space-y-4">
        <p className="text-lg font-semibold">A Lecture for Physicians on the Future of Longevity Medicine</p>
        <p className="text-sm opacity-80">Presented by: Next Generation Medicine</p>
        <div className="pt-4 text-xs opacity-70">
          A clean, minimalist slide with comprehensive scientific foundation for GLP-1 applications in longevity medicine.
        </div>
      </div>
    )
  },
  {
    id: 2,
    title: "Speaker Disclosure",
    content: (
      <div className="text-left space-y-2 text-sm">
        <ul className="space-y-1">
          <li>• <strong>Next Generation Medicine Faculty</strong></li>
          <li>• This presentation will discuss both FDA-approved and investigational uses of GLP-1 receptor agonists.</li>
          <li>• The content is intended for educational purposes and is based on currently available peer-reviewed literature and clinical trial data.</li>
          <li>• All clinical decisions should be made in consultation with the patient and based on a comprehensive evaluation.</li>
          <li>• No relevant financial relationships with commercial interests to disclose.</li>
        </ul>
      </div>
    )
  },
  {
    id: 3,
    title: "The Longevity Paradigm Shift",
    content: (
      <div className="text-left space-y-3 text-sm">
        <p>Traditionally, medicine has operated on a reactive model, intervening after the onset of age-related diseases like Type 2 Diabetes, Cardiovascular Disease, and Neurodegeneration.</p>
        <p>Longevity medicine represents a paradigm shift towards a proactive, preventative framework. The goal is not just to extend lifespan (the number of years lived) but to maximize <strong>healthspan</strong>—the period of life spent in good health, free from chronic disease and disability.</p>
        <p>This requires targeting the fundamental biological processes of aging itself, rather than its individual disease manifestations.</p>
        <div className="bg-blue-900/30 p-3 rounded border-l-4 border-blue-400 mt-4">
          <p className="text-xs font-semibold">Key Takeaway: The primary therapeutic target in longevity medicine is the aging process. By modulating the core drivers of aging, we can potentially delay, prevent, or even reverse multiple age-related diseases simultaneously.</p>
        </div>
      </div>
    )
  },
  {
    id: 4,
    title: "A Primer on GLP-1",
    content: (
      <div className="text-left space-y-3 text-sm">
        <p><strong>Glucagon-Like Peptide-1 (GLP-1)</strong> is an incretin hormone secreted by L-cells of the distal ileum and colon in response to nutrient intake.</p>
        <div>
          <p className="font-semibold mb-2">Canonical Functions:</p>
          <ul className="space-y-1 text-xs">
            <li>• <strong>Glucose-dependent insulin secretion:</strong> Stimulates the pancreas to release insulin only when blood glucose is elevated.</li>
            <li>• <strong>Suppression of glucagon secretion:</strong> Reduces hepatic glucose output.</li>
            <li>• <strong>Delayed gastric emptying:</strong> Promotes satiety and reduces postprandial glucose spikes.</li>
            <li>• <strong>Central appetite suppression:</strong> Acts on hypothalamic centers to reduce food intake.</li>
          </ul>
        </div>
        <p><strong>GLP-1 Receptor Agonists (GLP-1 RAs)</strong> are synthetic analogs designed to mimic these effects with a longer half-life. While developed for Type 2 Diabetes and obesity, their therapeutic potential is far broader.</p>
        <div className="bg-amber-900/30 p-3 rounded border-l-4 border-amber-400 mt-4">
          <p className="text-xs font-semibold">Clinical Note: The glucose-dependent nature of insulin secretion is a key safety feature, minimizing the risk of hypoglycemia compared to other anti-diabetic agents like sulfonylureas.</p>
        </div>
      </div>
    )
  },
  {
    id: 5,
    title: "The Pleiotropic Effects of GLP-1 Agonists",
    content: (
      <div className="space-y-4">
        <p className="text-sm">The GLP-1 receptor is expressed in numerous tissues beyond the pancreas and gut, mediating a wide array of protective effects that are central to healthspan. This systemic action is what makes these molecules prime candidates for longevity interventions.</p>
        <div className="w-full max-w-2xl mx-auto">
          <svg width="100%" height="300" viewBox="0 0 800 450" xmlns="http://www.w3.org/2000/svg">
            <style>
              {`
                .title { font-family: 'Inter', sans-serif; font-size: 18px; font-weight: 600; text-anchor: middle; fill: #000000; }
                .organ { font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 500; text-anchor: middle; fill: #000000; }
                .desc { font-family: 'Inter', sans-serif; font-size: 10px; text-anchor: middle; fill: #000000; }
                .line { stroke: #475569; stroke-width: 1.5; stroke-dasharray: 3 2; opacity: 0.7; }
                .center-circle { fill: #3b82f6; stroke: #1e40af; stroke-width: 2; }
                .center-text { font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 600; fill: #000000; text-anchor: middle; }
                .icon-circle { fill: #f1f5f9; stroke: #94a3b8; stroke-width: 1; }
              `}
            </style>
            <text x="400" y="30" className="title">GLP-1 Receptor Activation: Systemic Healthspan Benefits</text>
            
            {/* Central Hub */}
            <circle cx="400" cy="190" r="50" className="center-circle"/>
            <text x="400" y="185" className="center-text">GLP-1R</text>
            <text x="400" y="200" className="center-text">Activation</text>
            
            {/* Brain */}
            <line x1="400" y1="190" x2="250" y2="80" className="line"/>
            <circle cx="250" cy="80" r="30" className="icon-circle"/>
            <text x="250" y="77" className="organ">Brain</text>
            <text x="250" y="90" className="desc">Neuroprotection</text>
            <text x="250" y="102" className="desc">↓ Neuroinflammation</text>
            
            {/* Heart */}
            <line x1="400" y1="190" x2="550" y2="80" className="line"/>
            <circle cx="550" cy="80" r="30" className="icon-circle"/>
            <text x="550" y="77" className="organ">Heart</text>
            <text x="550" y="90" className="desc">Cardioprotection</text>
            <text x="550" y="102" className="desc">↓ Inflammation</text>
            
            {/* Liver */}
            <line x1="400" y1="190" x2="250" y2="300" className="line"/>
            <circle cx="250" cy="300" r="30" className="icon-circle"/>
            <text x="250" y="297" className="organ">Liver</text>
            <text x="250" y="310" className="desc">↓ Steatosis</text>
            <text x="250" y="322" className="desc">↓ Fibrosis</text>
            
            {/* Adipose Tissue */}
            <line x1="400" y1="190" x2="550" y2="300" className="line"/>
            <circle cx="550" cy="300" r="30" className="icon-circle"/>
            <text x="550" y="290" className="organ">Adipose</text>
            <text x="550" y="302" className="organ">Tissue</text>
            <text x="550" y="318" className="desc">↓ Inflammation</text>
            <text x="550" y="330" className="desc">↑ Adiponectin</text>
            
            {/* Pancreas */}
            <line x1="400" y1="190" x2="400" y2="300" className="line"/>
            <circle cx="400" cy="300" r="30" className="icon-circle"/>
            <text x="400" y="297" className="organ">Pancreas</text>
            <text x="400" y="312" className="desc">↑ β-cell function</text>
            <text x="400" y="324" className="desc">↓ Apoptosis</text>
          </svg>
        </div>
      </div>
    )
  },
  {
    id: 6,
    title: "The Hallmarks of Aging",
    content: (
      <div className="text-left space-y-3 text-sm">
        <p>The "Hallmarks of Aging" provide a biological roadmap for understanding and targeting the aging process. These are nine interconnected biological processes that deteriorate with age and, when modulated, can influence healthspan.</p>
        <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-xs">
          <div className="space-y-1">
            <p>1. Genomic Instability</p>
            <p>2. Telomere Attrition</p>
            <p>3. Epigenetic Alterations</p>
            <p>4. Loss of Proteostasis</p>
            <p>5. <strong className="text-yellow-300">Deregulated Nutrient Sensing</strong></p>
          </div>
          <div className="space-y-1">
            <p>6. <strong className="text-yellow-300">Mitochondrial Dysfunction</strong></p>
            <p>7. <strong className="text-yellow-300">Cellular Senescence</strong></p>
            <p>8. Stem Cell Exhaustion</p>
            <p>9. <strong className="text-yellow-300">Altered Intercellular Communication</strong></p>
          </div>
        </div>
        <div className="bg-blue-900/30 p-3 rounded border-l-4 border-blue-400 mt-4">
          <p className="text-xs font-semibold">Key Takeaway: GLP-1 agonists do not target all hallmarks, but they powerfully modulate at least four key, interconnected hallmarks (highlighted in bold), making them a potent anti-aging intervention.</p>
        </div>
      </div>
    )
  },
  {
    id: 7,
    title: "Modulating Mitochondrial Dysfunction & Nutrient Sensing",
    content: (
      <div className="space-y-4">
        <div className="text-left space-y-2 text-sm">
          <p><strong>Mitochondrial dysfunction</strong> is a core driver of aging, leading to reduced energy production and increased oxidative stress. <strong>Deregulated nutrient sensing</strong> (e.g., insulin resistance) accelerates aging. GLP-1 RAs directly address both.</p>
          <p>GLP-1 receptor activation enhances mitochondrial biogenesis and function, primarily through the <strong>PGC-1α pathway</strong>, the master regulator of mitochondrial health.</p>
        </div>
        <div className="w-full max-w-3xl mx-auto">
          <svg width="100%" height="280" viewBox="0 0 800 450" xmlns="http://www.w3.org/2000/svg">
            <style>
              {`
                .header { font-family: 'Inter', sans-serif; font-size: 16px; font-weight: 600; text-anchor: middle; fill: #e2e8f0; }
                .label { font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 500; fill: #334155; }
                .arrow { stroke: #3b82f6; stroke-width: 2; fill: none; marker-end: url(#arrowhead); }
                .box { fill: #f1f5f9; stroke: #94a3b8; stroke-width: 1; rx: 4; ry: 4; }
                .box-text { font-family: 'Inter', sans-serif; font-size: 10px; text-anchor: middle; fill: #0f172a; }
                .pathway-text { font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 600; fill: #3b82f6; }
                .outcome-text { font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 500; fill: #059669; }
                .problem-text { font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 500; fill: #dc2626; }
              `}
            </style>
            <defs>
              <marker id="arrowhead" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#3b82f6" />
              </marker>
            </defs>

            <text x="400" y="30" className="header">GLP-1 RAs Enhance Mitochondrial Function</text>

            {/* Pathway boxes */}
            <rect x="50" y="80" width="100" height="40" className="box"/>
            <text x="100" y="105" className="box-text">GLP-1 Agonist</text>
            
            <rect x="200" y="80" width="100" height="40" className="box"/>
            <text x="250" y="105" className="box-text">GLP-1 Receptor</text>
            
            <rect x="50" y="160" width="100" height="40" className="box"/>
            <text x="100" y="185" className="box-text">cAMP</text>
            
            <rect x="200" y="160" width="100" height="40" className="box"/>
            <text x="250" y="180" className="box-text">PKA / EPAC</text>
            <text x="250" y="195" className="box-text">Activation</text>

            <rect x="125" y="240" width="100" height="40" className="box" style={{fill: '#dbeafe', stroke: '#3b82f6'}}/>
            <text x="175" y="265" className="box-text" style={{fontWeight: 'bold'}}>PGC-1α</text>

            {/* Arrows */}
            <path d="M 155 100 L 195 100" className="arrow"/>
            <path d="M 250 125 L 250 155" className="arrow"/>
            <path d="M 100 125 L 100 155" className="arrow"/>
            <path d="M 100 205 L 140 235" className="arrow"/>
            <path d="M 250 205 L 210 235" className="arrow"/>

            {/* Outcomes */}
            <rect x="380" y="120" width="300" height="200" className="box" style={{strokeWidth: 0, fill: 'rgba(241, 245, 249, 0.5)'}}/>
            <text x="530" y="110" className="pathway-text">Downstream Effects of PGC-1α</text>

            <text x="400" y="150" className="outcome-text">↑ Mitochondrial Biogenesis</text>
            <text x="400" y="170" className="outcome-text">↑ Fatty Acid Oxidation</text>
            <text x="400" y="190" className="outcome-text">↑ Antioxidant Defenses (SOD, GPx)</text>
            <text x="400" y="210" className="problem-text">↓ Oxidative Stress (ROS)</text>
            <text x="400" y="230" className="problem-text">↓ Apoptosis</text>

            <path d="M 230 260 L 375 190" className="arrow"/>
          </svg>
        </div>
        <div className="bg-green-900/30 p-3 rounded border-l-4 border-green-400">
          <p className="text-xs font-semibold">Evidence: Preclinical studies consistently show that GLP-1 RAs like liraglutide increase PGC-1α expression, leading to improved mitochondrial respiration and reduced reactive oxygen species (ROS) production in various tissues, including the heart and brain. (Lee et al., 2016; Cunnane et al., 2020).</p>
        </div>
      </div>
    )
  },
  {
    id: 8,
    title: "Quelling 'Inflammaging' & Cellular Senescence",
    content: (
      <div className="space-y-4">
        <div className="text-left space-y-2 text-sm">
          <p><strong>Altered intercellular communication</strong>, particularly chronic, low-grade inflammation ("inflammaging"), is a major driver of age-related pathology. <strong>Cellular senescence</strong> contributes to this by secreting a pro-inflammatory cocktail known as the Senescence-Associated Secretory Phenotype (SASP).</p>
          <p>GLP-1 RAs exhibit powerful anti-inflammatory effects, independent of weight loss, by inhibiting key inflammatory pathways like NF-κB.</p>
        </div>
        <div className="w-full max-w-3xl mx-auto">
          <svg width="100%" height="280" viewBox="0 0 800 450" xmlns="http://www.w3.org/2000/svg">
            <style>
              {`
                .header { font-family: 'Inter', sans-serif; font-size: 16px; font-weight: 600; text-anchor: middle; fill: #000000; }
                .label { font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 500; fill: #000000; }
                .arrow { stroke: #3b82f6; stroke-width: 2; fill: none; marker-end: url(#arrowhead); }
                .inhibit { stroke: #dc2626; stroke-width: 2; fill: none; marker-end: url(#inhibithead); }
                .box { fill: #f1f5f9; stroke: #94a3b8; stroke-width: 1; rx: 4; ry: 4; }
                .box-text { font-family: 'Inter', sans-serif; font-size: 10px; text-anchor: middle; fill: #000000; }
                .cell-label { font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 600; text-anchor: middle; }
              `}
            </style>
            <defs>
              <marker id="arrowhead" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#3b82f6" />
              </marker>
              <marker id="inhibithead" viewBox="0 0 10 10" refX="1" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
                <path d="M 0 5 L 10 5" stroke="#dc2626" strokeWidth="2"/>
                <path d="M 0 0 L 0 10" stroke="#dc2626" strokeWidth="2"/>
              </marker>
            </defs>

            <text x="400" y="30" className="header">GLP-1 RAs Reduce Systemic Inflammation</text>

            {/* Macrophage/Immune Cell */}
            <path d="M 150,200 C 80,200 80,130 150,130 C 220,130 220,165 180,200 C 150,235 220,270 150,270 C 80,270 80,200 150,200 Z" fill="#fbbf24" opacity="0.3" stroke="#f59e0b" strokeWidth="2"/>
            <text x="150" y="205" className="cell-label" fill="#000000">Macrophage</text>

            {/* Pathway */}
            <rect x="250" y="80" width="100" height="40" className="box"/>
            <text x="300" y="105" className="box-text">GLP-1 Agonist</text>
            
            <path d="M 245 100 L 190 140" className="arrow"/>
            <text x="210" y="125" className="label">GLP-1R</text>

            <rect x="250" y="180" width="100" height="40" className="box" style={{fill: '#fef2f2', stroke: '#dc2626'}}/>
            <text x="300" y="205" className="box-text" style={{fontWeight: 'bold'}}>NF-κB Pathway</text>

            <rect x="250" y="280" width="100" height="40" className="box"/>
            <text x="300" y="305" className="box-text">cAMP / PKA</text>
            
            <path d="M 170 165 L 270 185" className="inhibit"/>
            <path d="M 170 235 L 270 285" className="arrow"/>

            {/* Inflammatory Cytokines */}
            <rect x="450" y="120" width="200" height="160" className="box" style={{strokeWidth: 0, fill: 'rgba(241, 245, 249, 0.5)'}}/>
            <text x="550" y="110" className="label" style={{fontWeight: 'bold', fill: '#dc2626'}}>Pro-inflammatory Cytokines (SASP)</text>
            <text x="470" y="145" className="box-text" style={{textAnchor: 'start'}}>• TNF-α</text>
            <text x="470" y="165" className="box-text" style={{textAnchor: 'start'}}>• IL-6</text>
            <text x="470" y="185" className="box-text" style={{textAnchor: 'start'}}>• IL-1β</text>
            <text x="470" y="205" className="box-text" style={{textAnchor: 'start'}}>• MCP-1</text>

            <path d="M 355 200 L 445 200" className="inhibit"/>
          </svg>
        </div>
        <div className="bg-amber-900/30 p-3 rounded border-l-4 border-amber-400">
          <p className="text-xs font-semibold">Clinical Note: This anti-inflammatory action is believed to underpin many of the observed benefits in cardiovascular, renal, and neurodegenerative models. Markers like hs-CRP are often reduced in patients on GLP-1 RAs.</p>
        </div>
      </div>
    )
  },
  {
    id: 9,
    title: "Improving Cardiovascular Healthspan",
    content: (
      <div className="text-left space-y-3 text-sm">
        <p>Cardiovascular disease remains the leading cause of mortality. Large-scale cardiovascular outcome trials (CVOTs) have provided robust evidence that GLP-1 RAs reduce major adverse cardiovascular events (MACE).</p>
        <div className="bg-slate-800 p-3 rounded">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-600">
                <th className="text-left pb-1">Trial</th>
                <th className="text-left pb-1">Drug</th>
                <th className="text-left pb-1">Population</th>
                <th className="text-left pb-1">MACE Reduction</th>
              </tr>
            </thead>
            <tbody className="space-y-1">
              <tr>
                <td className="py-1 font-semibold">LEADER</td>
                <td>Liraglutide</td>
                <td>T2D + High CV Risk</td>
                <td className="text-green-400 font-semibold">13% (HR 0.87)</td>
              </tr>
              <tr>
                <td className="py-1 font-semibold">SUSTAIN-6</td>
                <td>Semaglutide</td>
                <td>T2D + High CV Risk</td>
                <td className="text-green-400 font-semibold">26% (HR 0.74)</td>
              </tr>
              <tr>
                <td className="py-1 font-semibold">REWIND</td>
                <td>Dulaglutide</td>
                <td>T2D + Mixed Risk</td>
                <td className="text-green-400 font-semibold">12% (HR 0.88)</td>
              </tr>
              <tr>
                <td className="py-1 font-semibold">SELECT</td>
                <td>Semaglutide</td>
                <td>Obesity (No T2D)</td>
                <td className="text-green-400 font-semibold">20% (HR 0.80)</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="bg-blue-900/30 p-3 rounded border-l-4 border-blue-400">
          <p className="text-xs font-semibold">Key Takeaway: The SELECT trial was a landmark study. It demonstrated for the first time that a GLP-1 RA (semaglutide) reduces cardiovascular events in patients with obesity without diabetes, proving the benefits extend beyond glycemic control and are likely linked to reductions in weight, inflammation, and direct vascular effects.</p>
        </div>
      </div>
    )
  },
  {
    id: 10,
    title: "A New Hope for Neuroprotection",
    content: (
      <div className="space-y-4">
        <div className="text-left space-y-2 text-sm">
          <p>Neurodegenerative diseases like Alzheimer's and Parkinson's share pathological features with metabolic disease, including insulin resistance, inflammation, and mitochondrial dysfunction. This has led to the term "Type 3 Diabetes" for Alzheimer's Disease.</p>
          <p>GLP-1 receptors are highly expressed in the brain, particularly in the hippocampus and cortex.</p>
        </div>
        <div className="w-full max-w-3xl mx-auto">
          <svg width="100%" height="280" viewBox="0 0 800 450" xmlns="http://www.w3.org/2000/svg">
            <style>
              {`
                .header { font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 600; text-anchor: middle; fill: #000000; }
                .label { font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 600; fill: #000000; text-anchor: middle; }
                .desc { font-family: 'Inter', sans-serif; font-size: 9px; fill: #000000; text-anchor: middle; }
                .inhibit { stroke: #dc2626; stroke-width: 2; fill: none; marker-end: url(#inhibithead); }
                .neuron-body { fill: #3b82f6; opacity: 0.2; stroke: #1e40af; stroke-width: 2; }
                .axon { stroke: #1e40af; stroke-width: 2; fill: none; }
              `}
            </style>
            <defs>
              <marker id="inhibithead" viewBox="0 0 10 10" refX="1" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
                <path d="M 0 5 L 10 5" stroke="#dc2626" strokeWidth="2"/>
                <path d="M 0 0 L 0 10" stroke="#dc2626" strokeWidth="2"/>
              </marker>
              <marker id="arrowhead" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#059669" />
              </marker>
            </defs>

            <text x="400" y="30" className="header">Potential Neuroprotective Mechanisms of GLP-1 Agonists</text>

            {/* Neuron */}
            <path className="neuron-body" d="M 200,190 C 150,120 250,120 200,190" />
            <path className="axon" d="M 200,190 Q 300,215 400,190 T 600,190" />
            <text x="180" y="175" style={{fontSize: '12px', fontWeight: 'bold', fill: '#000000'}}>Neuron</text>

            {/* GLP-1 Input */}
            <text x="100" y="80" className="label">GLP-1 Agonist</text>
            <path d="M 120,90 L 170,150" style={{stroke: '#3b82f6', strokeWidth: 2, markerEnd: 'url(#arrowhead)'}} />

            {/* Mechanisms */}
            <g transform="translate(280, 80)">
              <path d="M 0,0 L 40,40" className="inhibit" />
              <text x="50" y="45" className="label" style={{textAnchor: 'start', fill: '#000000'}}>↓ Aβ Plaque Burden</text>
              <text x="50" y="60" className="desc" style={{textAnchor: 'start'}}>(Reduces production, enhances clearance)</text>
            </g>
            <g transform="translate(280, 140)">
              <path d="M 0,0 L 40,40" className="inhibit" />
              <text x="50" y="45" className="label" style={{textAnchor: 'start', fill: '#000000'}}>↓ Tau Hyperphosphorylation</text>
              <text x="50" y="60" className="desc" style={{textAnchor: 'start'}}>(Inhibits GSK3β kinase)</text>
            </g>
            <g transform="translate(280, 200)">
              <path d="M 0,0 L 40,40" className="inhibit" />
              <text x="50" y="45" className="label" style={{textAnchor: 'start', fill: '#000000'}}>↓ Neuroinflammation</text>
              <text x="50" y="60" className="desc" style={{textAnchor: 'start'}}>(Suppresses microglial activation)</text>
            </g>
            <g transform="translate(280, 260)">
              <path d="M 0,0 L 40,40" style={{stroke: '#059669', strokeWidth: 2, markerEnd: 'url(#arrowhead)'}} />
              <text x="50" y="45" className="label" style={{textAnchor: 'start', fill: '#000000'}}>↑ Synaptic Plasticity</text>
              <text x="50" y="60" className="desc" style={{textAnchor: 'start'}}>(Promotes LTP and neurogenesis)</text>
            </g>
          </svg>
        </div>
        <div className="bg-green-900/30 p-3 rounded border-l-4 border-green-400">
          <p className="text-xs font-semibold">Evidence: Large-scale Phase 3 trials, EVOKE and EVOKE Plus, are currently investigating semaglutide for early-stage Alzheimer's disease. Results are highly anticipated and could revolutionize how we approach neurodegeneration.</p>
        </div>
      </div>
    )
  },
  {
    id: 11,
    title: "Reversing Metabolic Dysfunction-Associated Steatohepatitis (MASH)",
    content: (
      <div className="text-left space-y-3 text-sm">
        <p>MASH (formerly NASH) is a silent epidemic driven by metabolic dysfunction, and it is a major contributor to cirrhosis and liver cancer. It is fundamentally a disease of ectopic fat storage and inflammation.</p>
        <div>
          <p className="font-semibold mb-2">GLP-1 RAs target the core pathophysiology of MASH:</p>
          <ol className="space-y-1 text-xs">
            <li>1. <strong>Reduce Caloric Intake:</strong> Leading to weight loss and mobilization of hepatic fat.</li>
            <li>2. <strong>Improve Insulin Sensitivity:</strong> Reducing de novo lipogenesis in the liver.</li>
            <li>3. <strong>Direct Anti-inflammatory Effects:</strong> Reducing hepatic inflammation and potentially fibrosis.</li>
          </ol>
        </div>
        <div className="bg-green-900/30 p-3 rounded border-l-4 border-green-400">
          <p className="text-xs font-semibold">Evidence: A Phase 2 trial with semaglutide showed that 59% of patients achieved MASH resolution without worsening of fibrosis, compared to 17% with placebo (p&lt;0.001). This was a groundbreaking result, leading to ongoing Phase 3 trials. (Newsome et al., 2021).</p>
        </div>
        <div className="bg-amber-900/30 p-3 rounded border-l-4 border-amber-400">
          <p className="text-xs font-semibold">Clinical Note: Given the lack of approved therapies for MASH, GLP-1 RAs are already being used off-label and are poised to become a first-line treatment, significantly impacting liver-related healthspan.</p>
        </div>
      </div>
    )
  },
  {
    id: 12,
    title: "The Evolution of Incretin Therapy",
    content: (
      <div className="text-left space-y-3 text-sm">
        <p>The field is rapidly evolving beyond single-target GLP-1 agonism. By co-targeting other metabolic hormone receptors, we can achieve synergistic effects on weight loss, metabolic control, and potentially, healthspan.</p>
        <div className="space-y-3">
          <div>
            <p className="font-semibold text-blue-300">Generation 1: GLP-1 Mono-Agonists</p>
            <ul className="text-xs space-y-1 ml-4">
              <li>• <strong>Examples:</strong> Liraglutide, Semaglutide, Dulaglutide</li>
              <li>• <strong>Mechanism:</strong> Selective GLP-1 receptor activation</li>
              <li>• <strong>Primary Effects:</strong> Excellent glycemic control, significant weight loss (~5-15%), proven CV benefits</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-green-300">Generation 2: Dual GIP/GLP-1 Agonists</p>
            <ul className="text-xs space-y-1 ml-4">
              <li>• <strong>Example:</strong> Tirzepatide</li>
              <li>• <strong>Mechanism:</strong> Activates both GLP-1 and GIP receptors. GIP enhances the insulinotropic and weight loss effects of GLP-1</li>
              <li>• <strong>Primary Effects:</strong> Superior glycemic control and weight loss (~15-22%) compared to GLP-1 mono-agonists</li>
            </ul>
          </div>
        </div>
        <div className="bg-blue-900/30 p-3 rounded border-l-4 border-blue-400">
          <p className="text-xs font-semibold">Key Takeaway: The success of Tirzepatide proved that a multi-agonist approach could yield superior results, setting the stage for the next generation of metabolic medicines.</p>
        </div>
      </div>
    )
  },
  {
    id: 13,
    title: "The Next Frontier: Retatrutide",
    content: (
      <div className="text-left space-y-3 text-sm">
        <p><strong>Retatrutide</strong> is an investigational triple-agonist that activates the receptors for <strong>GLP-1, GIP, and Glucagon</strong>. The addition of glucagon agonism is a paradigm shift.</p>
        <p>While traditionally viewed as a counter-regulatory hormone that raises blood sugar, at the right balance, glucagon signaling in the liver and adipose tissue provides unique benefits:</p>
        <ul className="text-xs space-y-1 ml-4">
          <li>• <strong>Increases Energy Expenditure:</strong> Boosts metabolic rate</li>
          <li>• <strong>Promotes Lipolysis:</strong> Directly stimulates the breakdown of fat in adipose tissue</li>
          <li>• <strong>Enhances Hepatic Fat Oxidation:</strong> Helps the liver burn fat for energy</li>
          <li>• <strong>Suppresses Appetite:</strong> Contributes to satiety</li>
        </ul>
        <div className="bg-amber-900/30 p-3 rounded border-l-4 border-amber-400">
          <p className="text-xs font-semibold">Clinical Note: The concern for hyperglycemia from glucagon is mitigated by the potent insulin-stimulating effects of the GLP-1 and GIP components, creating a balanced and powerful metabolic effect.</p>
        </div>
        <div className="bg-green-900/30 p-3 rounded border-l-4 border-green-400">
          <p className="text-xs font-semibold">Evidence: In a Phase 2 trial, patients on the highest dose of Retatrutide achieved an average of 24.2% weight loss at 48 weeks—a level approaching that of bariatric surgery. (Jastreboff et al., 2023).</p>
        </div>
      </div>
    )
  },
  {
    id: 14,
    title: "The Power of Multi-Agonism: A Comparative View",
    content: (
      <div className="space-y-4">
        <p className="text-sm">The progression from single to triple agonism represents a more holistic approach to metabolic regulation, targeting multiple pathways for a synergistic effect on healthspan.</p>
        <div className="w-full max-w-4xl mx-auto">
          <svg width="100%" height="300" viewBox="0 0 850 450" xmlns="http://www.w3.org/2000/svg">
            <style>
              {`
                .header { font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 600; text-anchor: middle; fill: #000000; }
                .drug-name { font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 600; text-anchor: middle; fill: #000000; }
                .receptor-label { font-family: 'Inter', sans-serif; font-size: 10px; font-weight: 500; fill: #000000; }
                .effect-label { font-family: 'Inter', sans-serif; font-size: 9px; fill: #000000; }
                .check { font-family: sans-serif; font-size: 16px; fill: #059669; }
                .line { stroke: #475569; stroke-width: 1; }
                .box { fill: #f8fafc; stroke: #cbd5e1; stroke-width: 1; rx: 6; ry: 6; }
              `}
            </style>

            {/* Column 1: Semaglutide */}
            <rect x="50" y="60" width="200" height="280" className="box"/>
            <text x="150" y="45" className="header">Semaglutide</text>
            <text x="150" y="85" className="drug-name">(GLP-1 Agonist)</text>
            <line x1="60" y1="100" x2="240" y2="100" className="line"/>
            <text x="70" y="125" className="receptor-label">GLP-1:</text><text x="110" y="125" className="check">✔</text>
            <text x="70" y="140" className="effect-label">↓ Appetite, ↑ Insulin</text>
            <text x="70" y="170" className="receptor-label">GIP:</text>
            <text x="70" y="210" className="receptor-label">Glucagon:</text>
            <line x1="60" y1="260" x2="240" y2="260" className="line"/>
            <text x="150" y="285" className="effect-label" style={{fontWeight: 'bold', fill: '#000000'}}>Excellent Weight Loss</text>
            <text x="150" y="300" className="effect-label" style={{fontWeight: 'bold', fill: '#000000'}}>(~15%)</text>

            {/* Column 2: Tirzepatide */}
            <rect x="275" y="60" width="200" height="280" className="box"/>
            <text x="375" y="45" className="header">Tirzepatide</text>
            <text x="375" y="85" className="drug-name">(GIP/GLP-1 Agonist)</text>
            <line x1="285" y1="100" x2="465" y2="100" className="line"/>
            <text x="295" y="125" className="receptor-label">GLP-1:</text><text x="335" y="125" className="check">✔</text>
            <text x="295" y="140" className="effect-label">↓ Appetite, ↑ Insulin</text>
            <text x="295" y="170" className="receptor-label">GIP:</text><text x="335" y="170" className="check">✔</text>
            <text x="295" y="185" className="effect-label">Enhances GLP-1 effects</text>
            <text x="295" y="210" className="receptor-label">Glucagon:</text>
            <line x1="285" y1="260" x2="465" y2="260" className="line"/>
            <text x="375" y="285" className="effect-label" style={{fontWeight: 'bold', fill: '#059669'}}>Superior Weight Loss</text>
            <text x="375" y="300" className="effect-label" style={{fontWeight: 'bold', fill: '#059669'}}>(~22%)</text>

            {/* Column 3: Retatrutide */}
            <rect x="500" y="60" width="200" height="280" className="box"/>
            <text x="600" y="45" className="header">Retatrutide</text>
            <text x="600" y="85" className="drug-name">(GLP-1/GIP/Glucagon)</text>
            <line x1="510" y1="100" x2="690" y2="100" className="line"/>
            <text x="520" y="125" className="receptor-label">GLP-1:</text><text x="560" y="125" className="check">✔</text>
            <text x="520" y="140" className="effect-label">↓ Appetite, ↑ Insulin</text>
            <text x="520" y="170" className="receptor-label">GIP:</text><text x="560" y="170" className="check">✔</text>
            <text x="520" y="185" className="effect-label">Enhances GLP-1 effects</text>
            <text x="520" y="210" className="receptor-label">Glucagon:</text><text x="580" y="210" className="check">✔</text>
            <text x="520" y="225" className="effect-label">↑ Energy Expenditure</text>
            <line x1="510" y1="260" x2="690" y2="260" className="line"/>
            <text x="600" y="285" className="effect-label" style={{fontWeight: 'bold', fill: '#059669'}}>Unprecedented Weight Loss</text>
            <text x="600" y="300" className="effect-label" style={{fontWeight: 'bold', fill: '#059669'}}>(&gt;24%)</text>
          </svg>
        </div>
      </div>
    )
  },
  {
    id: 15,
    title: "The Emerging Pipeline",
    content: (
      <div className="text-left space-y-3 text-sm">
        <p>The pace of innovation is not slowing down. Key developments to watch include:</p>
        <div className="space-y-3">
          <div>
            <p className="font-semibold text-blue-300">High-Bioavailability Oral Formulations:</p>
            <ul className="text-xs space-y-1 ml-4">
              <li>• Current oral semaglutide has &lt;1% bioavailability</li>
              <li>• Next-generation pills like <strong>orforglipron</strong> (Lilly) and <strong>danuglipron</strong> (Pfizer) are small molecules that do not require special dosing instructions</li>
              <li>• Novel delivery systems, like Biora Therapeutics' PGN-OB2 liquid jet delivery capsule, promise injectable-level bioavailability in a pill</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-green-300">Novel Combinations:</p>
            <ul className="text-xs space-y-1 ml-4">
              <li>• <strong>CagriSema (Novo Nordisk):</strong> A combination of semaglutide and cagrilintide (an amylin analog)</li>
              <li>• <strong>Survodutide (Boehringer Ingelheim):</strong> A dual GLP-1/glucagon receptor agonist</li>
              <li>• <strong>Mazdutide (Innovent Biologics):</strong> A dual GLP-1/glucagon receptor agonist showing promising results in China</li>
            </ul>
          </div>
        </div>
        <div className="bg-blue-900/30 p-3 rounded border-l-4 border-blue-400 mt-4">
          <p className="text-xs font-semibold">The future of metabolic medicine lies in precision targeting of multiple pathways simultaneously, with improved convenience and reduced side effects.</p>
        </div>
      </div>
    )
  }
];

const GLP1sAndLongevityAtriaV3: React.FC = () => {
  return (
    <MentorshipAccessGate>
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Header Section */}
        <section className="bg-gradient-to-b from-slate-50 to-white pt-24 pb-16">
          <div className="max-w-6xl mx-auto px-6">
            <Breadcrumb className="mb-8">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link href="/mentorship">Mentorship</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link href="/mentorship">Research Updates</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>GLP-1s and Longevity Atria V3</BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <div className="flex items-start gap-8">
              <div className="flex-1">
                <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight tracking-tight">
                  GLP-1s and Longevity Atria V3
                </h1>
                <p className="text-xl text-slate-600 mb-8 leading-relaxed font-light">
                  Advanced clinical perspectives on GLP-1 agonists for healthspan optimization with Nature/Cell journal-quality scientific visualizations
                </p>
                
                <div className="flex flex-wrap items-center gap-6 text-sm text-slate-500">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    <span className="font-medium">Research Update</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Book className="w-4 h-4" />
                    <span className="font-medium">30 min read</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-medium">Advanced Clinical Science</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="mb-12">
              <Link href="/mentorship">
                <Button variant="outline" size="sm" className="mb-8 hover:bg-slate-50">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Mentorship
                </Button>
              </Link>
            </div>

            {/* Introduction */}
            <div className="prose prose-slate max-w-none mb-16">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8 border border-blue-100">
                <h2 className="text-2xl font-bold text-slate-900 mb-4 tracking-tight">
                  A Scientific Journey Through GLP-1 Longevity Medicine
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  This advanced lecture presentation explores the cutting-edge science of GLP-1 receptor agonists in longevity medicine, 
                  featuring high-quality scientific visualizations and comprehensive clinical evidence. Designed for physicians seeking 
                  to understand the mechanistic foundations and clinical applications of these revolutionary therapeutics.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">15</div>
                    <div className="text-sm text-slate-600">Scientific Slides</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">8</div>
                    <div className="text-sm text-slate-600">Custom SVG Diagrams</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">30</div>
                    <div className="text-sm text-slate-600">Minutes Duration</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Slideshow */}
            <div className="mb-16">
              <Slideshow slides={slides} />
            </div>

            {/* Key Learning Points */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-6 tracking-tight">Key Learning Objectives</h3>
                <div className="space-y-4">
                  <KeyTakeaway>
                    Understand how GLP-1 agonists target fundamental aging processes through the hallmarks of aging framework
                  </KeyTakeaway>
                  <KeyTakeaway>
                    Master the mechanistic basis for pleiotropic effects across cardiovascular, neurological, and metabolic systems
                  </KeyTakeaway>
                  <KeyTakeaway>
                    Evaluate clinical evidence from major cardiovascular outcome trials and emerging therapeutic applications
                  </KeyTakeaway>
                  <KeyTakeaway>
                    Explore the evolution from single to multi-agonist therapies and their implications for healthspan optimization
                  </KeyTakeaway>
                </div>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-6 tracking-tight">Clinical Applications</h3>
                <div className="space-y-4">
                  <ClinicalNote>
                    Cardiovascular risk reduction in patients with and without diabetes, as demonstrated in SELECT trial
                  </ClinicalNote>
                  <ClinicalNote>
                    Neuroprotective applications in neurodegenerative diseases including ongoing Alzheimer's trials
                  </ClinicalNote>
                  <ClinicalNote>
                    Liver health optimization through MASH resolution and metabolic dysfunction correction
                  </ClinicalNote>
                  <Evidence>
                    Next-generation multi-agonist therapies achieving unprecedented weight loss approaching bariatric surgery levels
                  </Evidence>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-xl p-8 border border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-4 tracking-tight">
                The Future of Metabolic Medicine
              </h3>
              <p className="text-slate-600 leading-relaxed mb-6">
                GLP-1 receptor agonists represent a paradigm shift in our approach to aging and metabolic health. 
                By targeting fundamental biological processes rather than individual diseases, these therapeutics 
                offer unprecedented opportunities for healthspan optimization and longevity medicine applications.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Mechanistic Insights</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Mitochondrial biogenesis through PGC-1α pathway activation</li>
                    <li>• Anti-inflammatory effects via NF-κB pathway inhibition</li>
                    <li>• Neuroprotective mechanisms targeting amyloid and tau pathology</li>
                    <li>• Metabolic flexibility restoration and nutrient sensing optimization</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Clinical Impact</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• 13-26% reduction in major adverse cardiovascular events</li>
                    <li>• 59% MASH resolution in Phase 2 trials</li>
                    <li>• Up to 24% weight loss with triple-agonist therapies</li>
                    <li>• Promising results in neurodegenerative disease trials</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </MentorshipAccessGate>
  );
};

export default GLP1sAndLongevityAtriaV3;